import type {
  AdminGame,
  GameInput,
  GameProvider,
  MediaAsset,
  StorePlatform,
} from "@lucky-try/contracts";
import { gameInputSchema } from "@lucky-try/contracts";
import { ArrowLeftIcon, PlusIcon, TrashIcon } from "@phosphor-icons/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { toast } from "sonner";
import { ImagePickerField } from "../../components/media-picker";
import { Button } from "../../components/ui/button";
import { ConfirmDialog } from "../../components/ui/dialog";
import { Checkbox, Field, Input, Select, Textarea } from "../../components/ui/form";
import { Card, ErrorState, Loading, PageHeader, Tabs } from "../../components/ui/misc";
import { api, errorMessage } from "../../lib/api";
import {
  emptyVariant,
  normalizeMarketInput,
  scalarDefaults,
  scalarsFrom,
  STORE_PLATFORMS,
  toGameInput,
  variantsFrom,
  type ScalarValues,
  type StoreLinkForm,
  type VariantForm,
} from "../../lib/game-form";
import { categoriesQuery, gameQuery } from "../../lib/queries";
import { useCan } from "../../lib/session";

type Tab = "general" | "content" | "delivery" | "media" | "flags";

export function GameEditorPage() {
  const params = useParams({ strict: false }) as { gameId?: string };
  const gameId = params.gameId ? Number(params.gameId) : null;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const canWrite = useCan("catalog.write");
  const canPublish = useCan("catalog.publish");

  const [tab, setTab] = useState<Tab>("general");
  const [variants, setVariants] = useState<VariantForm[]>([]);
  const [storeLinks, setStoreLinks] = useState<StoreLinkForm[]>([]);
  const [icon, setIcon] = useState<MediaAsset | null>(null);
  const [preview, setPreview] = useState<MediaAsset | null>(null);
  const [thumbnail, setThumbnail] = useState<MediaAsset | null>(null);
  const [issues, setIssues] = useState<string[]>([]);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [loadedId, setLoadedId] = useState<number | null>(null);

  const categories = useQuery(categoriesQuery);
  const game = useQuery({ ...gameQuery(gameId ?? 0), enabled: gameId !== null });

  const form = useForm<ScalarValues>({ defaultValues: scalarDefaults });
  const { control, register, handleSubmit, reset } = form;
  const status = useWatch({ control, name: "status" });

  if (gameId !== null && game.data && loadedId !== gameId) {
    setLoadedId(gameId);
    reset(scalarsFrom(game.data));
    setVariants(variantsFrom(game.data));
    setStoreLinks(game.data.storeLinks);
    setIcon(game.data.icon);
    setPreview(game.data.preview);
    setThumbnail(game.data.thumbnail);
  }

  const save = useMutation({
    mutationFn: (body: GameInput) =>
      gameId === null
        ? api<AdminGame>("/admin/games", { body })
        : api<AdminGame>(`/admin/games/${gameId}`, { method: "PUT", body }),
    onSuccess: async (saved) => {
      await queryClient.invalidateQueries({ queryKey: ["games"] });
      await queryClient.invalidateQueries({ queryKey: ["game", saved.id] });
      toast.success("Game saved");
      if (gameId === null)
        await navigate({ to: "/games/$gameId", params: { gameId: String(saved.id) } });
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const remove = useMutation({
    mutationFn: () => api<void>(`/admin/games/${gameId}`, { method: "DELETE" }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["games"] });
      toast.success("Game deleted");
      await navigate({ to: "/games" });
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  function submit(values: ScalarValues) {
    const candidate = toGameInput(values, {
      iconAssetId: icon?.id ?? null,
      previewAssetId: preview?.id ?? null,
      thumbnailAssetId: thumbnail?.id ?? null,
      storeLinks,
      variants,
    });
    const parsed = gameInputSchema.safeParse(candidate);
    if (!parsed.success) {
      setIssues(
        parsed.error.issues.map((issue) => `${issue.path.join(".") || "form"}: ${issue.message}`),
      );
      toast.error("Fix the highlighted problems before saving");
      return;
    }
    setIssues([]);
    save.mutate(candidate);
  }

  if (game.error) return <ErrorState error={game.error} onRetry={() => void game.refetch()} />;
  if (gameId !== null && game.isPending) return <Loading />;

  return (
    <form onSubmit={handleSubmit(submit)}>
      <PageHeader
        title={gameId === null ? "New game" : (game.data?.name ?? "Game")}
        description={gameId === null ? "Draft a catalog entry." : `/${game.data?.slug}`}
        actions={
          <>
            <Link to="/games">
              <Button variant="ghost">
                <ArrowLeftIcon /> Back
              </Button>
            </Link>
            {gameId !== null && canWrite && (
              <Button variant="danger" onClick={() => setConfirmDelete(true)}>
                <TrashIcon /> Delete
              </Button>
            )}
            {canWrite && (
              <Button type="submit" variant="primary" disabled={save.isPending}>
                {save.isPending ? "Saving…" : "Save"}
              </Button>
            )}
          </>
        }
      />

      {status === "published" && !canPublish && (
        <p className="border-gold-line bg-gold-bg text-gold-text text-caption mb-3 rounded-md border px-3 py-2">
          Your role cannot publish; saving with status “published” will be rejected.
        </p>
      )}

      {issues.length > 0 && (
        <ul className="border-danger/30 bg-danger/10 text-danger-text text-caption mb-3 space-y-1 rounded-md border px-3 py-2">
          {issues.map((issue) => (
            <li key={issue}>{issue}</li>
          ))}
        </ul>
      )}

      <Tabs
        value={tab}
        onChange={setTab}
        tabs={[
          { id: "general", label: "General" },
          { id: "content", label: "Content & SEO" },
          { id: "delivery", label: `Delivery (${variants.length})` },
          { id: "media", label: "Media & links" },
          { id: "flags", label: "Flags" },
        ]}
      />

      {tab === "general" && (
        <Card className="max-w-[1060px]">
          <div className="grid gap-4 md:grid-cols-2">
            <Field label="Name">
              <Input {...register("name", { required: true })} />
            </Field>
            <Field label="Slug" hint="Public URL segment; changing it breaks existing links.">
              <Input {...register("slug", { required: true })} />
            </Field>
            <Field label="Status">
              <Select {...register("status")}>
                <option value="draft">Draft</option>
                <option value="published">Published</option>
                <option value="archived">Archived</option>
              </Select>
            </Field>
            <Field label="Category">
              <Select {...register("categoryId", { valueAsNumber: true })}>
                <option value={0}>Select a category…</option>
                {categories.data?.items.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Author">
              <Input {...register("author")} />
            </Field>
            <Field label="Release date">
              <Input type="date" {...register("releaseDate")} />
            </Field>
            <Field label="Orientation">
              <Select {...register("orientation")}>
                <option value="landscape">Landscape</option>
                <option value="portrait">Portrait</option>
              </Select>
            </Field>
            <Field label="Manual sort order" hint="Lower values surface first in curated listings.">
              <Input type="number" min={0} {...register("sortOrder", { valueAsNumber: true })} />
            </Field>
            <Field label="Background colour" hint="#RRGGBB behind the player frame.">
              <Input placeholder="#0f1220" {...register("backgroundColor")} />
            </Field>
          </div>
        </Card>
      )}

      {tab === "content" && (
        <Card className="max-w-[1060px]">
          <div className="grid gap-4">
            <Field label="Brief description" hint="Shown on cards and in listings.">
              <Textarea rows={2} {...register("briefDescription")} />
            </Field>
            <Field label="Description" hint="Limited HTML: paragraphs, lists, links, emphasis.">
              <Textarea rows={12} className="text-caption font-mono" {...register("description")} />
            </Field>
            <div className="grid gap-4 md:grid-cols-2">
              <Field label="SEO title" hint="Falls back to the game name.">
                <Input {...register("seoTitle")} />
              </Field>
              <Field label="SEO description">
                <Input {...register("seoDescription")} />
              </Field>
              <Field label="Rating value" hint="0–5, or empty when unrated.">
                <Input type="number" step="0.1" min={0} max={5} {...register("ratingValue")} />
              </Field>
              <Field label="Rating count">
                <Input type="number" min={0} {...register("ratingCount")} />
              </Field>
            </div>
          </div>
        </Card>
      )}

      {tab === "delivery" && (
        <Card
          title="Delivery variants"
          actions={
            canWrite && (
              <Button
                size="sm"
                onClick={() => setVariants((current) => [...current, emptyVariant()])}
              >
                <PlusIcon /> Add variant
              </Button>
            )
          }
        >
          {variants.length === 0 ? (
            <p className="text-muted text-caption">
              No variants yet. A published game needs at least one enabled variant; the “global”
              market is the fallback for every country without its own entry.
            </p>
          ) : (
            <div className="flex flex-col gap-3">
              {variants.map((variant, index) => {
                const update = (patch: Partial<VariantForm>) =>
                  setVariants((current) =>
                    current.map((item, position) =>
                      position === index ? { ...item, ...patch } : item,
                    ),
                  );
                return (
                  <div
                    key={variant.id ?? `new-${index}`}
                    className="border-line rounded-md border p-3"
                  >
                    <div className="grid gap-3 md:grid-cols-4">
                      <Field label="Market" hint="`global` or a 2-letter country code.">
                        <Input
                          value={variant.market}
                          onChange={(event) =>
                            update({ market: normalizeMarketInput(event.target.value) })
                          }
                        />
                      </Field>
                      <Field label="Provider">
                        <Select
                          value={variant.provider}
                          onChange={(event) =>
                            update({ provider: event.target.value as GameProvider })
                          }
                        >
                          <option value="gamedistribution">GameDistribution</option>
                          <option value="gamepush">GamePush</option>
                          <option value="external">External URL</option>
                          <option value="self_hosted">Self-hosted build</option>
                        </Select>
                      </Field>
                      <div className="flex items-end gap-3 md:col-span-2">
                        <Checkbox
                          label="Enabled"
                          checked={variant.isEnabled}
                          onChange={(event) => update({ isEnabled: event.target.checked })}
                        />
                        <Button
                          size="sm"
                          variant="ghost"
                          className="ml-auto"
                          onClick={() =>
                            setVariants((current) =>
                              current.filter((_, position) => position !== index),
                            )
                          }
                        >
                          <TrashIcon /> Remove
                        </Button>
                      </div>
                    </div>
                    <div className="mt-3 grid gap-3 md:grid-cols-2">
                      {variant.provider === "self_hosted" ? (
                        <>
                          <Field label="Build directory" hint="Folder under the games bucket.">
                            <Input
                              value={variant.directory}
                              onChange={(event) => update({ directory: event.target.value })}
                            />
                          </Field>
                          <Field label="Build version">
                            <Input
                              value={variant.version}
                              onChange={(event) => update({ version: event.target.value })}
                            />
                          </Field>
                        </>
                      ) : (
                        <Field label="Embed URL" className="md:col-span-2">
                          <Input
                            value={variant.url}
                            onChange={(event) => update({ url: event.target.value })}
                            placeholder="https://"
                          />
                        </Field>
                      )}
                      {variant.provider === "gamedistribution" && (
                        <Field label="Asset id" hint="32 hex characters, optional.">
                          <Input
                            value={variant.assetId}
                            onChange={(event) => update({ assetId: event.target.value.trim() })}
                          />
                        </Field>
                      )}
                      {variant.provider === "gamepush" && (
                        <>
                          <Field label="Project id">
                            <Input
                              value={variant.projectId}
                              onChange={(event) => update({ projectId: event.target.value.trim() })}
                            />
                          </Field>
                          <Field
                            label="Platform key"
                            hint="Defaults to the partner key configured on the API."
                          >
                            <Input
                              value={variant.platformKey}
                              onChange={(event) =>
                                update({ platformKey: event.target.value.trim() })
                              }
                            />
                          </Field>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      )}

      {tab === "media" && (
        <div className="grid gap-4 lg:grid-cols-2">
          <Card title="Images">
            <div className="grid gap-4 sm:grid-cols-3">
              <ImagePickerField
                label="Icon"
                hint="Required to publish."
                asset={icon}
                onChange={setIcon}
              />
              <ImagePickerField
                label="Preview"
                hint="Player splash."
                asset={preview}
                onChange={setPreview}
              />
              <ImagePickerField
                label="Thumbnail"
                hint="Wide card art."
                asset={thumbnail}
                onChange={setThumbnail}
              />
            </div>
          </Card>
          <Card
            title="Store links"
            actions={
              canWrite &&
              storeLinks.length < STORE_PLATFORMS.length && (
                <Button
                  size="sm"
                  onClick={() =>
                    setStoreLinks((current) => {
                      const used = new Set(current.map((link) => link.platform));
                      const next = STORE_PLATFORMS.find((platform) => !used.has(platform));
                      return next ? [...current, { platform: next, url: "" }] : current;
                    })
                  }
                >
                  <PlusIcon /> Add link
                </Button>
              )
            }
          >
            {storeLinks.length === 0 ? (
              <p className="text-muted text-caption">No store links.</p>
            ) : (
              <div className="flex flex-col gap-2">
                {storeLinks.map((link, index) => (
                  <div key={link.platform} className="flex items-center gap-2">
                    <Select
                      className="w-40"
                      value={link.platform}
                      onChange={(event) =>
                        setStoreLinks((current) =>
                          current.map((item, position) =>
                            position === index
                              ? { ...item, platform: event.target.value as StorePlatform }
                              : item,
                          ),
                        )
                      }
                    >
                      {STORE_PLATFORMS.map((platform) => (
                        <option key={platform} value={platform}>
                          {platform.replace("_", " ")}
                        </option>
                      ))}
                    </Select>
                    <Input
                      value={link.url}
                      placeholder="https://"
                      onChange={(event) =>
                        setStoreLinks((current) =>
                          current.map((item, position) =>
                            position === index ? { ...item, url: event.target.value } : item,
                          ),
                        )
                      }
                    />
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() =>
                        setStoreLinks((current) =>
                          current.filter((_, position) => position !== index),
                        )
                      }
                    >
                      <TrashIcon />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      )}

      {tab === "flags" && (
        <Card className="max-w-[1060px]">
          <div className="flex flex-col gap-3">
            <Checkbox label="Popular" {...register("isPopular")} />
            <Checkbox label="New" {...register("isNew")} />
            <Checkbox label="Original (made in-house)" {...register("isOriginal")} />
            <Checkbox label="Eligible for AdSense" {...register("adsenseEligible")} />
            <Checkbox
              label="Hide the adblock notice on this game"
              {...register("disableAdblockNotice")}
            />
          </div>
        </Card>
      )}

      <ConfirmDialog
        open={confirmDelete}
        title="Delete game"
        message="The catalog entry, its variants and likes are removed. Published builds stay in storage."
        confirmLabel="Delete"
        danger
        pending={remove.isPending}
        onConfirm={() => remove.mutate()}
        onClose={() => setConfirmDelete(false)}
      />
    </form>
  );
}
